import React from 'react';
import { LiteralUnion } from 'type-fest';
import { Checkbox, Select } from '@meitu/saas-ui';
import IconFont from '@meitu/xdesign-icon-font';
import './index.less';

export const allFilterKey = '__all__';

type FilterKey = LiteralUnion<typeof allFilterKey, string>;

export interface Filter {
    key: FilterKey;
    label: React.ReactNode;
}

interface OnChangeParams {
    changedFilters: { filterKey: FilterKey; checked: boolean }[] | null;
    changedFilterKey: FilterKey | null;
    checked: boolean;
}

export interface CheckBoxWithFilterProps {
    checkBoxName: string;
    checkBoxValue: boolean;
    currentFilterKey?: FilterKey;
    filters?: Filter[];
    onChange: (params: OnChangeParams) => void;
    onDropdownVisibleChange?: (visible: boolean) => void;
}

const CheckBoxWithFilter: React.FC<CheckBoxWithFilterProps> = ({
    checkBoxName,
    checkBoxValue,
    currentFilterKey = allFilterKey,
    filters,
    onChange,
    onDropdownVisibleChange,
}) => {
    return (
        <div className="cbwf-container">
            <Checkbox
                className="cbwf-checkbox"
                checked={checkBoxValue}
                onChange={(e) => {
                    const { checked } = e.target;
                    if (!filters) {
                        onChange({ changedFilters: null, changedFilterKey: null, checked });
                        return;
                    }
                    const changedFilters = checked
                        ? filters
                              .filter((filter) => {
                                  if (currentFilterKey === allFilterKey) {
                                      return true;
                                  }
                                  return filter.key === currentFilterKey;
                              })
                              .map((filter) => ({ filterKey: filter.key, checked: true }))
                        : currentFilterKey === allFilterKey
                          ? filters.map((filter) => ({ filterKey: filter.key, checked: false }))
                          : filters
                                .filter((filter) => filter.key !== allFilterKey)
                                .map((filter) => ({ filterKey: filter.key, checked }));
                    onChange({ changedFilters, changedFilterKey: null, checked });
                }}
            >
                {checkBoxName}
            </Checkbox>
            {!!filters?.length && (
                <Select
                    className="cbwf-select"
                    popupClassName="cbwf-select__dropdown"
                    size="small"
                    suffixIcon={<IconFont name="ChevronDownBlack" fontSize={10} color="var(--content-input-label)" />}
                    value={currentFilterKey}
                    options={filters.map((filter) => ({ value: filter.key, label: filter.label }))}
                    onDropdownVisibleChange={onDropdownVisibleChange}
                    onChange={(value: FilterKey) => {
                        if (!checkBoxValue) {
                            onChange({ changedFilters: null, changedFilterKey: value, checked: checkBoxValue });
                            return;
                        }
                        if (checkBoxValue && currentFilterKey === allFilterKey) {
                            const changedFilters = filters
                                .filter((filter) => filter.key !== value)
                                .map((filter) => ({ filterKey: filter.key, checked: false }));
                            onChange({ changedFilters, changedFilterKey: value, checked: checkBoxValue });
                            return;
                        }
                        if (checkBoxValue && currentFilterKey !== allFilterKey && value === allFilterKey) {
                            const changedFilters = filters
                                .filter((filter) => filter.key !== currentFilterKey)
                                .map((filter) => ({ filterKey: filter.key, checked: true }));
                            onChange({ changedFilters, changedFilterKey: value, checked: checkBoxValue });
                            return;
                        }
                        if (checkBoxValue && currentFilterKey !== allFilterKey) {
                            const changedFilters = filters
                                .filter((filter) => filter.key !== allFilterKey)
                                .map((filter) => ({ filterKey: filter.key, checked: filter.key === value }));
                            onChange({ changedFilters, changedFilterKey: value, checked: checkBoxValue });
                            return;
                        }
                        onChange({
                            changedFilters: filters.map((filter) => ({
                                filterKey: filter.key,
                                checked: !checkBoxValue,
                            })),
                            changedFilterKey: value,
                            checked: checkBoxValue,
                        });
                    }}
                />
            )}
        </div>
    );
};

CheckBoxWithFilter.displayName = 'CheckBoxWithFilter';

export default CheckBoxWithFilter;
